/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import {
  Divider,
  List,
  Typography,
  Tooltip,
  Skeleton,
  Select,
  Form,
  notification,
  Modal,
  Button,
  Table,
  Input,
} from 'antd';
import {
  EyeOutlined,
  SearchOutlined,
  CloseOutlined,
  CheckOutlined,
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { Truck } from 'lucide-react';
import ShipmentLogs from './ShipmentLogs';
import {
  SHIPMENT_PRIORITY_OPTIONS,
  SHIPMENT_CATEGORY_OPTIONS,
  SHIPMENT_PACKAGING_OPTIONS,
  normalizeShipmentMetaFromRecord,
  packagingFormValueToApi,
  getPopupContainerBody,
} from './shipmentFieldOptions';
import {
  OnDeleteShipmentNumber,
  OnFetchShipmentSno,
  OnAddShipmentNumber,
  OnPreviewShipmentNumber,
  OnAddorUpdateShipmentNumber,
} from '../../redux/ShipmentDetails/saga';

const { Title, Text } = Typography;
const { Option } = Select;

const ShipmentNo = (props) => {
  const { selectedData } = props;

  const [searchOpen, setSearchOpen] = useState(false);
  const [searchSno, setSearchSno] = useState(false);
  const [equipSnoDrop, setEquipSnoDrop] = useState([]);
  const [equipSnoList, setEquipSnoList] = useState([]);
  const [equipSnoSearchForm] = Form.useForm();
  const [isTableModelVisible, setIsTableModelVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedDataSno, setSelectedDataSno] = useState('');
  const [fetchLoading, setFetchLoading] = useState(false);
  const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);
  const [previewShipmentNumber, setPreviewShipmentNumber] = useState('');
  const [addLoading, setAddLoading] = useState(false);
  const [numSearch, setNumSearch] = useState('');
  const [filteredData, setFilteredData] = useState([]);

  // Helper for rendering searchable column titles matching Heat Profile style
  const renderColumnTitle = (title, onSearch, width = '100%') => (
    <div className="column-header-with-filter">
      <div className="column-header-text-wrapper">
        <span>{title}</span>
      </div>
      <div className="header-filter-input">
        <Input
          placeholder="Search"
          prefix={<SearchOutlined style={{ color: '#94a3b8', fontSize: '12px' }} />}
          onChange={(e) => onSearch(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          style={{ width: width === '100%' ? '140px' : width }}
          size="small"
          allowClear
        />
      </div>
    </div>
  );

  // Local filtering logic
  useEffect(() => {
    let data = equipSnoDrop || [];
    if (numSearch) {
      data = data.filter(item =>
        item.shipmentNumber?.toLowerCase().includes(numSearch.toLowerCase())
      );
    }
    setEquipSnoList(data);
  }, [equipSnoDrop, numSearch]);

  useEffect(() => {
    if (selectedData) OnfetchData();
  }, [selectedData]);

  useEffect(() => {
    if (selectedData) {
      const fetchShippingForms = async () => {
        try {
          setFetchLoading(true);
          await OnfetchData();
          setFetchLoading(false);
        } catch (error) {
          console.error('Failed to fetch shipping forms:', error);
          setFetchLoading(false);
        }
      };

      fetchShippingForms();
    }
  }, [selectedData]);

  const handleEyeClick = async (record) => {
    setSelectedDataSno(record);
    setIsTableModelVisible(true);
  };

  const OnfetchData = async () => {
    const obj = {
      id: selectedData.id,
    };
    const res = await OnFetchShipmentSno(obj);
    if (res['shipmentType']?.shipments && res['shipmentType'].shipments.length > 0) {
      setEquipSnoList(res['shipmentType'].shipments);
      setEquipSnoDrop(res['shipmentType'].shipments);
    }
  };

  const showDeleteModal = (record) => {
    setSelectedDataSno(record);
    setIsDeleteModalVisible(true);
  };

  const openEditShipmentNumber = (record) => {
    setSelectedDataSno(record);
    setEditShipmentNumber(record.shipmentNumber || '');
    const meta = normalizeShipmentMetaFromRecord(record);
    setEditPriority(meta.priority);
    setEditCategory(meta.shipmentCategory);
    setEditPackaging(meta.packagingSubcategory);
    setIsEditModalVisible(true);
  };

  const handleEditOk = async () => {
    const trimmed = String(editShipmentNumber || '').trim();
    if (!trimmed) {
      notification.warning({ message: 'Please enter a shipment number.' });
      return;
    }
    if (!editPackaging) {
      notification.warning({ message: 'Please select packaging (subcategory).' });
      return;
    }
    setEditLoading(true);
    const obj = {
      id: selectedDataSno.id,
      data: {
        shipment: {
          shipmentTypeId: selectedData.id,
          shipmentNumber: trimmed,
          priority: editPriority,
          shipmentCategory: editCategory,
          packagingType: packagingFormValueToApi(editPackaging),
        },
      },
    };
    const res = await OnAddorUpdateShipmentNumber(obj, true);
    if (res) await OnfetchData();
    setEditLoading(false);
    setIsEditModalVisible(false);
    setSelectedDataSno('');
  };

  const handleEditCancel = () => {
    setIsEditModalVisible(false);
    setSelectedDataSno('');
    setEditShipmentNumber('');
    setEditPackaging(undefined);
  };

  const handleOk = async () => {
    setDeleteLoading(true);
    await OnDeleteShipmentNumber(selectedDataSno);
    await OnfetchData();
    setDeleteLoading(false);
    setIsDeleteModalVisible(false);
  };

  const handleCancel = () => {
    setIsDeleteModalVisible(false);
    setSelectedDataSno('');
  };

  // Step 1: Initial click to preview shipment number
  const handleAddButtonClick = async () => {
    if (!selectedData || !selectedData.id) {
      notification.error({
        message: 'Cannot add shipment number',
        description: 'No shipment type selected',
      });
      return;
    }

    setAddLoading(true);

    try {
      // Call the preview endpoint to get the next number
      const previewData = await OnPreviewShipmentNumber(selectedData.id);

      if (previewData && previewData.previewShipmentNumber) {
        setPreviewShipmentNumber(previewData.previewShipmentNumber);
        setConfirmPriority('medium');
        setConfirmCategory('project');
        setConfirmPackaging(undefined);
        setIsConfirmModalVisible(true);
      } else {
        notification.error({
          message: 'Failed to generate shipment number preview',
        });
      }
    } catch (error) {
      notification.error({
        message: 'Error generating shipment number',
        description: error?.message || 'Unknown error',
      });
    } finally {
      setAddLoading(false);
    }
  };

  // Step 2: Handle confirmation (Yes/No)
  const handleConfirmAdd = async () => {
    if (!confirmPackaging) {
      notification.warning({ message: 'Please select packaging (subcategory).' });
      return;
    }
    const num = String(previewShipmentNumber || '').trim();
    if (!num) {
      notification.warning({ message: 'Please enter a shipment number.' });
      return;
    }
    setAddLoading(true);

    const payload = {
      shipment: {
        shipmentTypeId: selectedData.id,
        shipmentNumber: num,
        priority: confirmPriority,
        shipmentCategory: confirmCategory,
        packagingType: packagingFormValueToApi(confirmPackaging),
      },
    };

    const response = await OnAddShipmentNumber(payload);

    if (response?.shipment?.shipmentNumber) {
      notification.success({
        message: `Shipment number ${response.shipment.shipmentNumber} added successfully`,
      });
    }

    // Close the modal
    setIsConfirmModalVisible(false);

    await OnfetchData();
    setAddLoading(false);
  };

  const handleCancelAdd = () => {
    setIsConfirmModalVisible(false);
    setPreviewShipmentNumber('');
    setConfirmPackaging(undefined);
  };

  const onSelectEquipSno = () => {
    setSearchSno(true);
  };

  const onSearchEquipmentSno = () => {
    equipSnoSearchForm.validateFields().then(async (data) => {
      if (data && data.shipmentNumber) {
        setSearchSno(false);
        const newArray = equipSnoDrop.find((u) => u.shipmentNumber === data.shipmentNumber);
        setEquipSnoList([newArray]);
      } else {
        notification.warning({
          message: 'Select Equipment Sno to Search.',
        });
      }
    });
  };

  const columns = [
    {
      title: renderColumnTitle('Shipment Number', setNumSearch, '180px'),
      dataIndex: 'shipmentNumber',
      key: 'shipmentNumber',
      sorter: (a, b) => a.shipmentNumber.localeCompare(b.shipmentNumber),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '120px',
      align: 'center',
      render: (text, record) => (
        <div className="heat-profile-actions-cell" style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
          <Tooltip title="View History">
            <EyeOutlined
              className="txtIcon"
              style={{ color: '#00A86B', fontSize: '18px', cursor: 'pointer' }}
              onClick={() => handleEyeClick(record)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <DeleteOutlined
              className="txtIcon"
              style={{ color: '#ef314c', fontSize: '18px', cursor: 'pointer' }}
              onClick={() => showDeleteModal(record)}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div className="heat-profile-card" style={{ marginTop: '20px' }}>
      <div className="heat-profile-list-header">
        <div className="heat-profile-header-left">
          <div className="heat-profile-icon-wrap">
            <Truck size={20} color="#ef314c" />
          </div>
          <div className="heat-profile-header-content">
            <Title level={4} className="heat-profile-list-title">Shipment Numbers</Title>
            <p className="heat-profile-list-description">Manage individual shipment units for this type.</p>
          </div>
        </div>

        <div className="heat-profile-header-actions">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddButtonClick}
            className="heat-profile-add-btn"
          >
            Add Number
          </Button>
        </div>
      </div>

      <div className="heat-profile-table-wrap">
        <Table
          className="heat-profile-table"
          columns={columns}
          dataSource={equipSnoList}
          rowKey="id"
          loading={fetchLoading}
          pagination={{ pageSize: 10 }}
          size="middle"
        />
      </div>

      {isTableModelVisible && (
        <ShipmentLogs
          showFormModel={isTableModelVisible}
          setShowFormModel={setIsTableModelVisible}
          selectedData={selectedDataSno}
        />
      )}

      {/* Modernized Preview Modal */}
      <Modal
        width={500}
        className="heatprofile-modal"
        title="Confirm New Shipment Number"
        centered
        visible={isConfirmModalVisible}
        onCancel={handleCancelAdd}
        footer={[
          <Button key="cancel" onClick={handleCancelAdd}>Cancel</Button>,
          <Button
            key="confirm"
            type="primary"
            onClick={handleConfirmAdd}
            loading={addLoading}
            disabled={!confirmPackaging || !String(previewShipmentNumber || '').trim()}
          >
            Confirm Create
          </Button>,
        ]}
      >
        <div style={{ padding: '20px 0', textAlign: 'center' }}>
          <Text style={{ fontSize: '15px' }}>Do you want to add the following shipment number?</Text>
          <Title level={2} style={{ color: '#000000', marginTop: '10px', fontWeight: 'bold' }}>
            {previewShipmentNumber}
          </Title>
        </div>
      </Modal>

      {/* Modernized Delete Modal */}
      <Modal
        width={500}
        className="heatprofile-modal"
        title="Delete Shipment Number"
        centered
        visible={isDeleteModalVisible}
        onCancel={handleCancel}
        footer={[
          <Button key="cancel" onClick={handleCancel}>Cancel</Button>,
          <Button
            key="delete"
            type="primary"
            danger
            onClick={handleOk}
            loading={deleteLoading}
          >
            Delete
          </Button>,
        ]}
      >
        <div style={{ padding: '10px 0' }}>
          <Text style={{ fontSize: '15px', color: '#333' }}>
            <p>Deleting shipment number will remove all corresponding stations and related information.</p>
            Are you sure you want to delete <strong style={{ color: '#ef314c' }}>{selectedDataSno?.shipmentNumber}</strong>?
          </Text>
        </div>
      </Modal>
    </div>
  );
};

export default ShipmentNo;
